/**
 * QuizLandingTemplate - A reusable template for all quiz landing pages
 *
 * This template eliminates code duplication across quiz landing pages by providing
 * a configurable component that handles all common functionality including:
 * - User authentication and profile management
 * - Quiz initialization and navigation
 * - Shared quiz creation
 * - Platform-specific logic (native vs web)
 * - Consistent UI with quiz-specific styling
 *
 * Usage:
 * 1. Fetch quiz configuration from remoteQuizService
 * 2. Pass the configuration to the template
 *
 * Example:
 * ```
 * import React, { useState, useEffect } from "react";
 * import QuizLandingTemplate from "../../templates/QuizLandingTemplate";
 * import remoteQuizService from "../../services/RemoteQuizService";
 *
 * const YourQuizLanding = () => {
 *   const [config, setConfig] = useState(null);
 *   useEffect(() => {
 *     remoteQuizService.getQuizConfig("your-quiz-type").then(setConfig);
 *   }, []);
 *   if (!config) return <LoadingSpinner />;
 *   return (
 *     <QuizLandingTemplate
 *       quizConfig={config}
 *       specialHeaderConfig={config.specialHeaderConfig}
 *       hasNativePlatformLogic={config.hasNativePlatformLogic}
 *     />
 *   );
 * };
 * ```
 */

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";

import { Capacitor } from "@capacitor/core";
import { Clipboard } from "@capacitor/clipboard";

import {
  addDoc,
  arrayUnion,
  collection,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { signInAnonymously, signOut } from "firebase/auth";

import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import { TextField } from "@mui/material";

import OpenInAppBanner from "../components/layout/OpenInAppBanner";
import { ProPic, SecPic } from "../components/theme/ProfilePics";
import { LandingPageBox } from "../components/theme/QuizBox";
import LayoutQuiz from "../components/layout/QuizLayouts/layoutQuiz";
import {
  D1,
  GradientSpan,
  H1,
  P2,
  P3,
} from "../components/theme/text/Headings";
import { ButtonGradient } from "../components/theme/Button";
import {
  DarkGrey,
  gradientColors,
  Grey,
  OffWhite,
  White,
  White05,
} from "../components/theme/colors";
import QuizMetaData from "../components/QuizMetaData";

import { auth, db } from "../services/firebase.config";
import { StyledLink } from "../pages/Home";

const QuizLandingTemplate = ({
  quizConfig,
  specialHeaderConfig = null,
  hasNativePlatformLogic = false,
}) => {
  const navigate = useNavigate();
  const currentUser = auth.currentUser;
  const isNative = Capacitor.isNativePlatform();

  // Sender info
  const [displayName, setDisplayName] = useState("");
  const [photoURL, setPhotoURL] = useState("");
  const [senderUid, setSenderUid] = useState("");

  // Receiver info
  const [receiverName, setReceiverName] = useState("");
  const [receiverPhoto, setReceiverPhoto] = useState("");
  const [searchParams] = useSearchParams();

  // Quiz completion status
  const [bothUsersCompletedQuiz, setBothUsersCompletedQuiz] = useState(false);

  // Check if user is trying to take quiz with themselves
  const isOwnQuiz = senderUid && currentUser && senderUid === currentUser.uid;

  // Link copied state
  const [linkCopied, setLinkCopied] = useState(false);

  const fromQuizId = searchParams.get("from");

  // Send data when submitting name
  const authenticateAndSetDisplayName = async () => {
    try {
      let user = auth.currentUser;

      if (!user) {
        // If the user is not logged in, create an anonymous account
        const signInResult = await signInAnonymously(auth);
        user = signInResult.user;

        // Update Firestore with the new user's profile
        const userDocRef = doc(db, "public-profiles", user.uid);
        await setDoc(
          userDocRef,
          {
            uid: user.uid,
            displayName: receiverName,
          },
          { merge: true }
        );
      } else {
        // If the user is already logged in, update their profile if necessary
        const userDocRef = doc(db, "public-profiles", user.uid);
        const userDoc = await getDoc(userDocRef);

        if (!userDoc.exists()) {
          // Create a profile if it doesn't exist
          await setDoc(
            userDocRef,
            {
              uid: user.uid,
              displayName: receiverName,
            },
            { merge: true }
          );
        }
      }

      // Get the receiver's quiz ID from their profile
      const receiverProfileRef = doc(db, "public-profiles", user.uid);
      const receiverProfileDoc = await getDoc(receiverProfileRef);
      const receiverQuizId =
        receiverProfileDoc.exists() &&
        receiverProfileDoc.data()[quizConfig.databaseField];

      // DEDUPLICATION: Check for existing shared quiz between sender and receiver for this quiz
      // We need to consider both sender/receiver orderings
      const candidateQueries = [
        query(
          collection(db, "shared-quizzes"),
          where("senderUid", "==", senderUid),
          where("receiverUid", "==", user.uid),
          where("quizName", "==", quizConfig.quizName)
        ),
        query(
          collection(db, "shared-quizzes"),
          where("senderUid", "==", user.uid),
          where("receiverUid", "==", senderUid),
          where("quizName", "==", quizConfig.quizName)
        ),
      ];

      let existingSharedId = null;
      try {
        const [snap1, snap2] = await Promise.all([
          getDocs(candidateQueries[0]),
          getDocs(candidateQueries[1]),
        ]);

        const candidates = [...snap1.docs, ...snap2.docs];
        // If either side has already stored their quiz IDs, prefer the one matching quiz IDs; otherwise any match is fine
        if (candidates.length > 0) {
          const exactMatch = candidates.find((d) => {
            const data = d.data();
            return (
              (data.senderQuizId === fromQuizId &&
                data.receiverQuizId === (receiverQuizId || null)) ||
              (data.receiverQuizId === fromQuizId &&
                data.senderQuizId === (receiverQuizId || null))
            );
          });
          existingSharedId = (exactMatch || candidates[0]).id;
        }
      } catch (_) {
        // Ignore query errors; fall back to creation path
      }

      if (existingSharedId) {
        // Navigate directly to existing shared quiz
        return navigate(
          `${quizConfig.resultsRoute}?shared=${existingSharedId}`
        );
      }

      // Create a new shared quiz
      const newSharedRef = await addDoc(collection(db, "shared-quizzes"), {
        quizName: quizConfig.quizName,
        receiverUid: user.uid,
        receiverQuizId: receiverQuizId || null,
        senderUid: senderUid,
        senderQuizId: fromQuizId,
        created: serverTimestamp(),
      });

      // If the receiver already has a quiz ID and it's completed, update shared arrays and navigate to results
      if (receiverQuizId) {
        const receiverQuizRef = doc(db, "quiz-answers", receiverQuizId);
        const receiverQuizDoc = await getDoc(receiverQuizRef);

        if (
          receiverQuizDoc.exists() &&
          receiverQuizDoc.data().status === "completed"
        ) {
          // Mark shared as completed immediately so backend notifier fires
          await updateDoc(newSharedRef, {
            completed: true,
            completedAt: serverTimestamp(),
          });

          // Update shared array for both sender and receiver
          const senderProfileRef = doc(db, "public-profiles", senderUid);

          await Promise.all([
            updateDoc(senderProfileRef, {
              shared: arrayUnion(newSharedRef.id),
            }),
            updateDoc(receiverProfileRef, {
              shared: arrayUnion(newSharedRef.id),
            }),
          ]);

          // Handle navigation based on quiz type and platform
          if (quizConfig.hasSpecialWebLogic) {
            const isWeb = Capacitor.getPlatform() === "web";
            const isAnonymous = user?.isAnonymous;

            if (isWeb && isAnonymous) {
              // Anonymous users on web need to save answers first
              return navigate("/save-account", {
                state: { linkAnonymous: true },
              });
            }
          }

          if (hasNativePlatformLogic && isNative) {
            // Navigate to results page for native users
            return navigate(
              `${quizConfig.resultsRoute}?shared=${newSharedRef.id}`
            );
          } else if (hasNativePlatformLogic && !isNative) {
            // For web users, redirect to download app page
            return navigate("/download-app");
          } else {
            // Standard navigation to results
            return navigate(
              `${quizConfig.resultsRoute}?shared=${newSharedRef.id}`
            );
          }
        }
      }

      // If no completed quiz, navigate to the first question
      navigate(`${quizConfig.firstQuestionRoute}?shared=${newSharedRef.id}`);
    } catch (error) {
      // Silently fail - navigation errors are rare and non-critical
    }
  };

  useEffect(() => {
    const fetchLoggedIn = async () => {
      // Don't auto-fill if it's their own quiz
      if (isOwnQuiz) {
        setReceiverName("");
        setReceiverPhoto("");
        return;
      }

      if (currentUser) {
        const userDocRef = doc(db, "public-profiles", currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setReceiverName(userData.displayName || "");
          setReceiverPhoto(userData.photoURL || "");
        } else {
          setReceiverName("");
        }
      }
    };

    fetchLoggedIn();
  }, [currentUser, isOwnQuiz]);

  // Clear receiver data if it's their own quiz
  useEffect(() => {
    if (isOwnQuiz) {
      setReceiverName("");
      setReceiverPhoto("");
    }
  }, [isOwnQuiz]);

  // Fetch sender data
  useEffect(() => {
    const fetchSenderData = async () => {
      if (fromQuizId) {
        const quizDocRef = doc(db, "quiz-answers", fromQuizId);
        const quizDoc = await getDoc(quizDocRef);

        if (quizDoc.exists()) {
          const userDocRef = doc(db, "public-profiles", quizDoc.data().uid);
          const userDoc = await getDoc(userDocRef);
          setDisplayName(userDoc.data().displayName || "");
          setPhotoURL(userDoc.data().photoURL || "");
          setSenderUid(userDoc.data().uid || "");
        }
      }
    };

    fetchSenderData();
  }, [fromQuizId]);

  // Check if both users have completed their quizzes
  useEffect(() => {
    const checkBothUsersCompletedQuiz = async () => {
      if (!fromQuizId || !currentUser) return;

      try {
        // Check sender's quiz completion
        const senderQuizDoc = await getDoc(doc(db, "quiz-answers", fromQuizId));
        const senderQuizCompleted =
          senderQuizDoc.exists() && senderQuizDoc.data().status === "completed";

        // Check receiver's quiz completion
        const receiverProfileRef = doc(db, "public-profiles", currentUser.uid);
        const receiverProfileDoc = await getDoc(receiverProfileRef);

        let receiverQuizCompleted = false;
        if (receiverProfileDoc.exists()) {
          const receiverQuizId =
            receiverProfileDoc.data()[quizConfig.databaseField];
          if (receiverQuizId) {
            const receiverQuizDoc = await getDoc(
              doc(db, "quiz-answers", receiverQuizId)
            );
            receiverQuizCompleted =
              receiverQuizDoc.exists() &&
              receiverQuizDoc.data().status === "completed";
          }
        }

        setBothUsersCompletedQuiz(senderQuizCompleted && receiverQuizCompleted);
      } catch (error) {
        // Silently fail - this is just for UI display
        setBothUsersCompletedQuiz(false);
      }
    };

    checkBothUsersCompletedQuiz();
  }, [fromQuizId, currentUser, quizConfig.databaseField]);

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      // Silently fail - logout errors don't prevent navigation
    }
  };

  const handleCopyLink = async () => {
    const quizLink = `https://www.vibecheckme.com/s/${quizConfig.quizType}/${displayName}?from=${fromQuizId}`;

    try {
      await Clipboard.write({
        string: quizLink,
      });

      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (error) {
      // Fallback for browsers that don't support Clipboard API
      alert(`Copy this link:\n${quizLink}`);
    }
  };

  // Get gradient config with fallback to Firestore config
  const getGradientConfig = () => {
    // Try static gradient colors first
    if (gradientColors[quizConfig.quizType]) {
      return gradientColors[quizConfig.quizType];
    }

    // Fall back to config's gradientColors (from Firestore)
    if (quizConfig.gradientColors) {
      return quizConfig.gradientColors;
    }

    // Default fallback
    return {
      background: "#3B96FF",
      transitionBackground: "#F951FF",
    };
  };

  const gradientConfig = getGradientConfig();

  return (
    <>
      <QuizMetaData
        quizType={quizConfig.quizType}
        senderName={displayName}
        quizConfig={quizConfig}
      />
      <LayoutQuiz isNoFooter={true}>
        <OpenInAppBanner />
        <Container>
          <Row className="mt-10 justify-content-center">
            <Col
              lg={{ offset: 4, span: 4 }}
              md={{ offset: 3, span: 6 }}
              sm={{ offset: 2, span: 8 }}
              xs={{ span: 12 }}
              className="text-align-center"
            >
              <LandingPageBox>
                <P2
                  color={specialHeaderConfig ? undefined : Grey}
                  style={{ fontWeight: "600" }}
                >
                  {specialHeaderConfig ? (
                    <GradientSpan
                      $background={specialHeaderConfig.background}
                      $transitionBackground={
                        specialHeaderConfig.transitionBackground
                      }
                    >
                      {isOwnQuiz
                        ? "Send your Vibe Check!"
                        : `${displayName || "Your friend"} wants a Vibe Check!`}
                    </GradientSpan>
                  ) : isOwnQuiz ? (
                    "Send your Vibe Check!"
                  ) : (
                    `${displayName || "Your friend"} wants a Vibe Check!`
                  )}
                </P2>

                <D1 className="mb-20 mt-15" color={White}>
                  What's your{quizConfig.titleBreak ? <br /> : " "}
                  <GradientSpan
                    $background={gradientConfig.background}
                    $transitionBackground={gradientConfig.transitionBackground}
                  >
                    {quizConfig.titleWord}
                  </GradientSpan>{" "}
                  vibe?
                </D1>

                <div className="display-flex mt-30">
                  {photoURL ? (
                    <ProPic
                      src={photoURL}
                      $borderColor={gradientConfig.background}
                    />
                  ) : (
                    <SecPic $borderColor={gradientConfig.background}>
                      <H1>{displayName ? displayName[0] : "?"}</H1>
                    </SecPic>
                  )}
                  <CompareArrowsIcon className="mt-30" />
                  {receiverPhoto ? (
                    <ProPic
                      src={receiverPhoto}
                      $borderColor={gradientConfig.transitionBackground}
                    />
                  ) : (
                    <SecPic $borderColor={gradientConfig.transitionBackground}>
                      <H1>{receiverName ? receiverName[0] : "?"}</H1>
                    </SecPic>
                  )}
                </div>

                <TextField
                  className="mt-50"
                  fullWidth
                  value={receiverName}
                  label={isOwnQuiz ? "Your friend's name here" : "Your Name"}
                  placeholder={isOwnQuiz ? "Your friend's name here" : ""}
                  disabled={isOwnQuiz}
                  onChange={(e) => setReceiverName(e.target.value)}
                  sx={{
                    "& .MuiInputBase-input": {
                      fontSize: "17px",
                      color: OffWhite,
                      background: DarkGrey,
                      borderRadius: "20px",
                      padding: "15px 20px",
                      ...(isOwnQuiz && {
                        "&::placeholder": {
                          color: White05,
                          opacity: 1,
                        },
                      }),
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "15px",
                      color: OffWhite,
                    },
                    "& .MuiInputLabel-root.Mui-disabled": {
                      color: White05,
                    },
                    "& .Mui-focused": {
                      color: OffWhite, // focus label
                      borderRadius: "20px",
                    },
                  }}
                />

                <ButtonGradient
                  $background={gradientConfig.background}
                  $transitionBackground={gradientConfig.transitionBackground}
                  className="mt-20 ml-10 mb-20"
                  width="100%"
                  onClick={
                    isOwnQuiz ? handleCopyLink : authenticateAndSetDisplayName
                  }
                >
                  {isOwnQuiz
                    ? linkCopied
                      ? "Link Copied! ✓"
                      : "Copy link to send 🎉"
                    : bothUsersCompletedQuiz
                    ? "Compare Results 👀"
                    : "Get Started"}
                </ButtonGradient>

                {!isNative && (
                  <div>
                    {currentUser && (
                      <StyledLink onClick={handleSignOut}>
                        <P3>
                          <GradientSpan
                            $background={gradientConfig.background}
                            $transitionBackground={
                              gradientConfig.transitionBackground
                            }
                          >
                            Not {receiverName || "your account"}? Log Out
                          </GradientSpan>
                        </P3>
                      </StyledLink>
                    )}
                  </div>
                )}
              </LandingPageBox>
            </Col>
          </Row>
        </Container>
      </LayoutQuiz>
    </>
  );
};

export default QuizLandingTemplate;
